import React, { useMemo, useState } from "react";
import { Calculator, IndianRupee, Percent, Calendar } from "lucide-react";
import ToolCard from "./ToolCard";
import { formatINR } from "./formatters";

// FV = P * (1 + r)^t
function lumpsumFV(P, annualRate, years) {
  const principal = Number(P);
  const r = Number(annualRate) / 100;
  const t = Number(years);
  if (principal <= 0 || t <= 0) return { fv: 0, gain: 0 };
  const fv = principal * Math.pow(1 + r, t);
  return { fv, gain: fv - principal };
}

export default function LumpsumCalculator() {
  const [amount, setAmount] = useState(200000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);

  const { fv, gain } = useMemo(
    () => lumpsumFV(Number(amount), Number(rate), Number(years)),
    [amount, rate, years]
  );

  return (
    <ToolCard
      title="Lumpsum Calculator"
      subtitle="Estimate growth for a one-time investment using expected return."
    >
      <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-950">
        <Calculator className="h-4 w-4" />
        One-time investing
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <Field
          label="Investment Amount"
          icon={<IndianRupee className="h-4 w-4 text-slate-500" />}
          value={amount}
          onChange={setAmount}
          min={0}
        />
        <Field
          label="Expected Return (p.a.)"
          icon={<Percent className="h-4 w-4 text-slate-500" />}
          value={rate}
          onChange={setRate}
          min={0}
          step="0.1"
        />
        <Field
          label="Time (years)"
          icon={<Calendar className="h-4 w-4 text-slate-500" />}
          value={years}
          onChange={setYears}
          min={0}
          step="1"
        />
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <ResultBox title="Future Value" value={formatINR(fv)} highlight />
        <ResultBox title="Estimated Gain" value={formatINR(gain)} />
      </div>

      <p className="mt-3 text-xs text-slate-500">
        Note: This is an estimate based on assumed returns.
      </p>
    </ToolCard>
  );
}

function Field({ label, icon, value, onChange, min = 0, step = "1" }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-700">{label}</label>
      <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
        {icon}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full text-sm outline-none"
          min={min}
          step={step}
        />
      </div>
    </div>
  );
}

function ResultBox({ title, value, highlight }) {
  return (
    <div className={`rounded-2xl p-4 ${highlight ? "bg-emerald-50" : "bg-slate-50"}`}>
      <div className={`text-xs ${highlight ? "text-emerald-900/70" : "text-slate-600"}`}>{title}</div>
      <div className={`mt-1 text-lg font-semibold ${highlight ? "text-emerald-950" : "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}
